import { FC, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Modal, IconButton } from '@mui/material';
import { ChevronLeft } from '@mui/icons-material';

import styles from './IdeaDetails.module.scss';
import { IdeaStopListItem } from '../../../types/idea.types';
import CustomButton from '../../../common/CustomButton';
import CustomInput from '../../../common/CustomInput';
import { useIsMobile } from '../../../hooks/useWindowWidth';
import { useDeleteIdeaStopMutation, useGetIdeaQuery, useGetIdeaStopQuery } from '../../../store/services/ideasApi';
import ConfirmDeleteModal from '../../../common/ConfirmDeleteModal/ConfirmDeleteModal';
import CreateIdeaStopModal from './components/CreateIdeaStopModal/CreateIdeaStopModal';
import IdeaStopDetails from './components/IdeaStopDetails/IdeaStopDetails';
import IdeaCard from './components/IdeaCard/IdeaCard';
import { SearchIcon } from '../../../components/Icons';
import { MOBILE_FULL_SCREEN_MODAL_Z, MODAL_ABOVE_FULL_SCREEN_Z } from '../../../constants/modalZIndex';

enum RightPanelContentType {
  HIDDEN = 'hidden',
  IDEA_STOP_DETAILS = 'idea_stop_details',
}

interface RightPanelState {
  isVisible: boolean;
  contentType: RightPanelContentType;
  data?: any;
}

const IdeaDetails: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [showCreateIdeaStopModal, setShowCreateIdeaStopModal] = useState(false);
  const [editingIdeaStopId, setEditingIdeaStopId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { data: ideaDetail, isLoading: ideaLoading, error: ideaError, refetch: refetchIdea } = useGetIdeaQuery(id!);
  const [deleteIdeaStop, { isLoading: isDeletingStop }] = useDeleteIdeaStopMutation();
  const [stopPendingDelete, setStopPendingDelete] = useState<IdeaStopListItem | null>(null);

  const { data: editingIdeaStopDetails } = useGetIdeaStopQuery(editingIdeaStopId?.toString() || '', {
    skip: !editingIdeaStopId,
  });

  const idea = ideaDetail;
  const ideaStops = ideaDetail?.idea_stops || [];
  const stopsLoading = ideaLoading;

  const [rightPanelState, setRightPanelState] = useState<RightPanelState>({
    isVisible: false,
    contentType: RightPanelContentType.HIDDEN,
    data: null,
  });

  const { data: selectedIdeaStopDetails } = useGetIdeaStopQuery(rightPanelState.data?.id?.toString() || '', {
    skip: !rightPanelState.data?.id,
  });

  const handleShowRightPanel = useCallback((contentType: RightPanelContentType, data?: any) => {
    setRightPanelState({
      isVisible: true,
      contentType,
      data,
    });
  }, []);

  const handleHideRightPanel = useCallback(() => {
    setRightPanelState({
      isVisible: false,
      contentType: RightPanelContentType.HIDDEN,
      data: null,
    });
  }, []);

  const handleShowIdeaStopDetails = useCallback(
    (ideaStop: IdeaStopListItem) => {
      handleShowRightPanel(RightPanelContentType.IDEA_STOP_DETAILS, ideaStop);
    },
    [handleShowRightPanel]
  );

  const handleBackClick = useCallback(() => {
    navigate('/ideas');
  }, [navigate]);

  const handleAddNewStop = useCallback(() => {
    handleHideRightPanel();
    setEditingIdeaStopId(null);
    setShowCreateIdeaStopModal(true);
  }, [handleHideRightPanel]);

  const handleCloseIdeaStopModal = useCallback(() => {
    setShowCreateIdeaStopModal(false);
    setEditingIdeaStopId(null);
  }, []);

  const handleIdeaStopCreated = useCallback(() => {
    console.log('Idea stop created successfully');
    refetchIdea();
  }, [refetchIdea]);

  const handleEditIdeaStop = useCallback(
    (ideaStop: IdeaStopListItem) => {
      handleHideRightPanel();
      setEditingIdeaStopId(ideaStop.id);
      setShowCreateIdeaStopModal(true);
    },
    [handleHideRightPanel]
  );

  const handleDeleteIdeaStopRequest = useCallback((ideaStop: IdeaStopListItem) => {
    setStopPendingDelete(ideaStop);
  }, []);

  const handleConfirmDeleteIdeaStop = useCallback(async () => {
    if (!stopPendingDelete) return;
    try {
      await deleteIdeaStop(stopPendingDelete.id.toString()).unwrap();
      refetchIdea();
      handleHideRightPanel();
      setStopPendingDelete(null);
    } catch (error) {
      console.error('Failed to delete idea stop:', error);
    }
  }, [stopPendingDelete, deleteIdeaStop, refetchIdea, handleHideRightPanel]);

  const handleRefetchIdea = useCallback(() => {
    refetchIdea();
  }, [refetchIdea]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const filteredIdeaStops = useMemo(() => {
    if (!searchQuery.trim()) {
      return ideaStops;
    }

    const query = searchQuery.toLowerCase().trim();
    return ideaStops.filter(stop => {
      const stopType = stop.stop_type.toLowerCase();
      const title = stop.title?.toLowerCase() || '';
      const description = stop.description?.toLowerCase() || '';

      return stopType.includes(query) || title.includes(query) || description.includes(query);
    });
  }, [ideaStops, searchQuery]);

  const renderRightPanelContent = () => {
    if (!rightPanelState.isVisible) return null;

    switch (rightPanelState.contentType) {
      case RightPanelContentType.IDEA_STOP_DETAILS:
        if (selectedIdeaStopDetails) {
          return (
            <IdeaStopDetails
              ideaStop={selectedIdeaStopDetails}
              onClose={handleHideRightPanel}
              onRefetchIdea={handleRefetchIdea}
              ideaId={parseInt(id!)}
              onEditRequest={handleEditIdeaStop}
              onDeleteRequest={handleDeleteIdeaStopRequest}
            />
          );
        }
        return (
          <Box className={styles.rightPanelContent}>
            <Typography variant="h6">Loading...</Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  const renderMobileModal = () => {
    if (!isMobile || !rightPanelState.isVisible) return null;

    return (
      <Modal
        open={rightPanelState.isVisible}
        onClose={handleHideRightPanel}
        sx={{ zIndex: MOBILE_FULL_SCREEN_MODAL_Z }}
        slotProps={{
          backdrop: { sx: { backgroundColor: 'rgba(34, 43, 58, 0.45)' } },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            maxHeight: '100dvh',
            bgcolor: '#fff',
            outline: 'none',
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            pt: `calc(12px + env(safe-area-inset-top, 0px))`,
            pr: `calc(16px + env(safe-area-inset-right, 0px))`,
            pb: `calc(20px + env(safe-area-inset-bottom, 0px))`,
            pl: `calc(16px + env(safe-area-inset-left, 0px))`,
          }}
        >
          {selectedIdeaStopDetails ? (
            <IdeaStopDetails
              ideaStop={selectedIdeaStopDetails}
              onClose={handleHideRightPanel}
              onRefetchIdea={handleRefetchIdea}
              ideaId={parseInt(id!)}
              onEditRequest={handleEditIdeaStop}
              onDeleteRequest={handleDeleteIdeaStopRequest}
              nestedModalZIndex={MODAL_ABOVE_FULL_SCREEN_Z}
            />
          ) : (
            <Typography variant="h6">Loading...</Typography>
          )}
        </Box>
      </Modal>
    );
  };

  const renderDesktopStopDetailsOverlay = () => {
    if (isMobile) return null;

    return (
      <Modal
        open={rightPanelState.isVisible}
        onClose={handleHideRightPanel}
        sx={{ zIndex: 1200 }}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: 'transparent' },
          },
        }}
      >
        <Box className={styles.ideaStopDetailsModalPanel}>
          <Box className={styles.ideaStopDetailsModalInner}>{renderRightPanelContent()}</Box>
        </Box>
      </Modal>
    );
  };

  if (ideaLoading) {
    return (
      <Box className={styles.ideaDetails}>
        <Box className={styles.loadingContainer}>
          <Typography variant="h6">Loading idea details...</Typography>
        </Box>
      </Box>
    );
  }

  if (ideaError || !idea) {
    return (
      <Box className={styles.ideaDetails}>
        <Box className={styles.errorContainer}>
          <Typography
            variant="h6"
            color="error"
            sx={{ mb: 2 }}
          >
            Idea not found
          </Typography>
          <CustomButton
            variant="outlined"
            onClick={handleBackClick}
          >
            Back to Ideas
          </CustomButton>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box className={styles.ideaDetails}>
        <Box className={styles.ideaContent}>
          <Box className={styles.contentWrapper}>
            <Box className={styles.leftColumn}>
              <Box className={styles.ideaHeader}>
                <Box className={styles.headerLeft}>
                  <IconButton
                    onClick={handleBackClick}
                    className={styles.backButton}
                    size="small"
                  >
                    <ChevronLeft />
                  </IconButton>
                  <Box className={styles.headerContent}>
                    <Typography
                      variant="h4Bold"
                      className={styles.ideaTitle}
                    >
                      {idea.name}
                    </Typography>
                  </Box>
                </Box>
                <CustomButton
                  variant="contained"
                  onClick={handleAddNewStop}
                  customClass={styles.createButton}
                >
                  {isMobile ? '+' : '+ Create New'}
                </CustomButton>
              </Box>

              <Box className={styles.searchWrapper}>
                <CustomInput
                  inputType="text"
                  inputName="search"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  startIcon={<SearchIcon size={20} />}
                  endIcon={
                    searchQuery ? (
                      <Box
                        onClick={clearSearch}
                        sx={{ cursor: 'pointer', color: '#5e6a81' }}
                      >
                        ×
                      </Box>
                    ) : undefined
                  }
                />
              </Box>

              <Box className={styles.ideaStopsContainer}>
                <Box className={styles.ideaStopsList}>
                  {stopsLoading && <Typography>Loading stops...</Typography>}

                  {filteredIdeaStops && filteredIdeaStops.length > 0 ? (
                    <Box className={styles.stopsGrid}>
                      {filteredIdeaStops.map(ideaStop => (
                        <IdeaCard
                          key={ideaStop.id}
                          ideaStop={ideaStop}
                          ideaId={parseInt(id!)}
                          isSelected={
                            rightPanelState.isVisible &&
                            rightPanelState.data?.id === ideaStop.id
                          }
                          onSelect={() => handleShowIdeaStopDetails(ideaStop)}
                          onEdit={handleEditIdeaStop}
                          onDeleteRequest={handleDeleteIdeaStopRequest}
                          onCoverUpdated={handleRefetchIdea}
                          onSentToTrip={handleRefetchIdea}
                          onChildModalOpen={handleHideRightPanel}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Box className={styles.emptyState}>
                      {searchQuery ? (
                        <>
                          <Typography variant="h7">No stops found</Typography>
                          <Typography
                            variant="h11"
                            sx={{ color: '#5E6A81' }}
                          >
                            Try adjusting your search
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography variant="h7">No stops added yet</Typography>
                          <Typography
                            variant="h11"
                            sx={{ color: '#5E6A81' }}
                          >
                            Start planning by adding your first stop!
                          </Typography>
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

          </Box>
        </Box>

        <CreateIdeaStopModal
          open={showCreateIdeaStopModal}
          onClose={handleCloseIdeaStopModal}
          onSuccess={handleIdeaStopCreated}
          ideaId={parseInt(id!)}
          editMode={!!editingIdeaStopId}
          ideaStopData={editingIdeaStopDetails}
        />

        <ConfirmDeleteModal
          open={stopPendingDelete !== null}
          onClose={() => !isDeletingStop && setStopPendingDelete(null)}
          message={
            stopPendingDelete
              ? `You want to remove the stop “${stopPendingDelete.title || 'Idea stop'}”?`
              : ''
          }
          confirmLabel="Delete stop"
          onConfirm={handleConfirmDeleteIdeaStop}
          isConfirming={isDeletingStop}
          zIndex={
            isMobile && rightPanelState.isVisible ? MODAL_ABOVE_FULL_SCREEN_Z : undefined
          }
        />

        {renderMobileModal()}
        {renderDesktopStopDetailsOverlay()}
      </Box>
    </>
  );
};

export default IdeaDetails;
